/* eslint-disable @typescript-eslint/no-empty-function */

import { captureMessage } from '@sentry/browser';
import axios from 'axios';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cookie from 'react-cookies';
import { unstable_batchedUpdates as batchUpdates } from 'react-dom';

import {
  LEGACY_ROLES,
  PERMISSIONS,
} from 'components/account/AccountView/users/constants';
import { LegacyRoles } from 'components/account/AccountView/users/types';
import { ViewOptions, VIEW_OPTIONS } from 'components/sales/constants';
import Constants from 'controllers/constants';
import { Requests } from 'controllers/network';
import { initializeAxiosClients } from 'controllers/network/apiClient';
import { logOutAccess, logOutRefresh } from 'controllers/utils';
import {
  discardLegacyHistoryGrant,
  isHistoryGrantId,
  revokeLegacyHistoryGrant,
} from 'controllers/legacyHistorySession';
import { User } from 'types';
import { isAccessExpired, parseJwt, reloadForAccessExpiry } from 'utils/auth';
import { createSafeContext } from 'utils/createSafeContext';
import { CSRF_TOKEN_NAME, IS_ADMIN_HOST } from 'utils/envVars';

export { parseJwt } from 'utils/auth';

const { internalApiHost } = Constants;

const REFRESH_TOKEN_ENDPOINT = '/token/refresh';
const REFRESH_RETRY_DELAY_MS = 250;
const HISTORY_SESSION_KEY = 'postscript:legacy-history-session';
const HISTORY_GRANT_KEY = 'postscript:legacy-history-grant';
const HISTORY_LOGOUT_KEY = 'postscript:legacy-history-logout';

const isTransientRefreshError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) return false;
  const status = error.response?.status;
  return typeof status === 'number'
    ? status === 429 || (status >= 500 && status < 600)
    : !!error.request;
};

interface RefreshOptions {
  rejectedAccessToken?: string;
  force?: boolean;
}

interface AuthSession {
  isHistorySession: boolean;
  accessToken: string | null;
  sessionVersion: number;
}

interface AuthContext {
  accessToken: string | null;
  sessionVersion: number;
  isLoading: boolean;
  isLoggedIn: boolean;
  isHistorySession: boolean;
  historyResumeError: boolean;
  historyLogoutPending: boolean;
  historyLogoutInFlight: boolean;
  failHistoryBootstrap: (token: string, error: unknown) => void;
  clearAccessToken: (expectedToken?: string) => void;
  saveAccessToken: (token: string | null, expiresIn?: number) => void;
  refreshAccessToken: (options?: RefreshOptions) => Promise<string | null>;
  logOut: () => Promise<boolean>;
  getAccessTokenPayload: () => Record<string, any> | undefined;
  getShopId: () => number;
  getUserPermissions: () => string[] | undefined;
  userHasPermission: (permission: string) => boolean;
  getLegacyUserRoles: () => LegacyRoles[];
  userHasLegacyRole: (role: LegacyRoles) => boolean;
  getMaxViewLevelForUser: (user: Partial<User>) => ViewOptions;
}

export const [Provider, useAuth] = createSafeContext<AuthContext>(
  'AuthContext',
  'useAuth must be a descendant of AuthContextProvider',
);

const createRefreshTokenConfig = () => {
  return {
    headers: {
      'X-CSRF-TOKEN': cookie.load(CSRF_TOKEN_NAME),
      // See internalApiAxiosClient in controllers/network/apiClient.
      ...(IS_ADMIN_HOST ? { 'X-Requested-With': 'XMLHttpRequest' } : {}),
    },
    withCredentials: true,
  };
};

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initial loading state while we check if the user is logged in
  const [isLoading, setIsLoading] = useState(true);
  const [historyResumeError, setHistoryResumeError] = useState(false);
  const [historyLogoutPending, setHistoryLogoutPending] = useState(
    () => sessionStorage.getItem(HISTORY_LOGOUT_KEY) === 'true',
  );
  const historyLogoutRef = useRef(historyLogoutPending);
  const [historyLogoutInFlight, setHistoryLogoutInFlight] = useState(false);
  const historyLogoutRequestRef = useRef<{
    version: number;
    promise: Promise<boolean>;
  } | null>(null);
  const historyDeadlineRef = useRef<number | null>(null);

  // React 17 does not batch async saves: the version must commit with its token.
  const [{ accessToken, sessionVersion, isHistorySession }, setSession] =
    useState<AuthSession>(() => ({
      isHistorySession:
        window.location.pathname === '/acorn/history' ||
        sessionStorage.getItem(HISTORY_SESSION_KEY) === 'true',
      accessToken: null,
      sessionVersion: 0,
    }));
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const accessTokenRef = useRef<string | null>(null);
  const historySessionRef = useRef(isHistorySession);
  const sessionVersionRef = useRef(0);
  const refreshRequestRef = useRef<{
    sessionVersion: number;
    promise: Promise<string | null>;
  } | null>(null);

  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cooldown to prevent rapid refresh attempts (e.g., if refreshed token is still rejected)
  const REFRESH_COOLDOWN_MS = 5000;
  const lastRefreshTimeRef = useRef<number>(0);
  const historyValidationRef = useRef<{ token: string; at: number } | null>(
    null,
  );

  const handleSetIsLoggedIn = useCallback((loggedIn: boolean) => {
    setIsLoggedIn(loggedIn);
  }, []);

  const setAccessToken = useCallback(
    (token: string | null) => {
      setHistoryResumeError(false);
      accessTokenRef.current = token;
      setSession({
        isHistorySession: historySessionRef.current,
        accessToken: token,
        sessionVersion: sessionVersionRef.current,
      });
      handleSetIsLoggedIn(!!token);
    },
    [handleSetIsLoggedIn],
  );

  const saveAccessToken = useCallback(
    (token: string | null, expiresIn?: number) => {
      if (token) {
        historyLogoutRef.current = false;
        setHistoryLogoutPending(false);
        setHistoryLogoutInFlight(false);
        sessionStorage.removeItem(HISTORY_LOGOUT_KEY);
        historySessionRef.current =
          !!parseJwt(token).identity?.acorn_legacy_viewer;
        if (historySessionRef.current) {
          const remaining =
            expiresIn ?? (parseJwt(token).exp * 1000 - Date.now()) / 1000;
          historyDeadlineRef.current =
            performance.now() + Math.max(0, remaining) * 1000;
          sessionStorage.setItem(HISTORY_SESSION_KEY, 'true');
          const { jti } = parseJwt(token);
          if (isHistoryGrantId(jti))
            sessionStorage.setItem(HISTORY_GRANT_KEY, jti);
          else sessionStorage.removeItem(HISTORY_GRANT_KEY);
        } else {
          historyDeadlineRef.current = null;
          sessionStorage.removeItem(HISTORY_SESSION_KEY);
          sessionStorage.removeItem(HISTORY_GRANT_KEY);
        }
      }
      historyValidationRef.current = null;
      sessionVersionRef.current += 1;
      lastRefreshTimeRef.current = 0;
      setAccessToken(token);
    },
    [setAccessToken],
  );

  const clearAccessToken = useCallback(
    (expectedToken?: string) => {
      if (expectedToken && accessTokenRef.current !== expectedToken) return;
      sessionStorage.removeItem(HISTORY_GRANT_KEY);
      historyDeadlineRef.current = null;
      saveAccessToken(null);
    },
    [saveAccessToken],
  );

  const logOut = useCallback(async () => {
    if (historySessionRef.current) {
      const pending = historyLogoutRequestRef.current;
      if (pending?.version === sessionVersionRef.current)
        return pending.promise;
      const grant = sessionStorage.getItem(HISTORY_GRANT_KEY);
      setHistoryLogoutInFlight(true);
      historyLogoutRef.current = true;
      setHistoryLogoutPending(true);
      sessionStorage.setItem(HISTORY_LOGOUT_KEY, 'true');
      // Pending logout keeps only the binding needed to retry revocation.
      saveAccessToken(null);
      const logoutVersion = sessionVersionRef.current;
      const promise: Promise<boolean> = (async () => {
        if (isHistoryGrantId(grant)) {
          try {
            await revokeLegacyHistoryGrant(grant);
          } catch (error) {
            if (!axios.isAxiosError(error) || error.response?.status !== 401) {
              try {
                captureMessage(
                  'Could not revoke Postscript 1.0 history session',
                  'warning',
                );
              } catch {
                /* Reporting must not discard pending logout. */
              }
              return false;
            }
          }
        }
        if (sessionVersionRef.current !== logoutVersion) return false;
        clearAccessToken();
        historyLogoutRef.current = false;
        setHistoryLogoutPending(false);
        sessionStorage.removeItem(HISTORY_LOGOUT_KEY);
        return true;
      })().finally(() => {
        if (historyLogoutRequestRef.current?.promise === promise) {
          historyLogoutRequestRef.current = null;
          setHistoryLogoutInFlight(false);
        }
      });
      historyLogoutRequestRef.current = { version: logoutVersion, promise };
      return promise;
    }
    await logOutAccess();

    clearAccessToken();

    await logOutRefresh();

    return true;
  }, [clearAccessToken, saveAccessToken]);

  const failHistoryBootstrap = useCallback(
    (token: string, error: unknown) => {
      if (accessTokenRef.current !== token) return;
      if (
        axios.isAxiosError(error) &&
        [401, 403].includes(error.response?.status || 0)
      ) {
        const grant = parseJwt(token).jti;
        clearAccessToken(token);
        if (isHistoryGrantId(grant)) discardLegacyHistoryGrant(grant);
      } else {
        batchUpdates(() => {
          saveAccessToken(null);
          setHistoryResumeError(true);
        });
      }
    },
    [clearAccessToken, saveAccessToken],
  );

  const refreshAccessToken = useCallback(
    (options?: RefreshOptions): Promise<string | null> => {
      const { current } = accessTokenRef;
      if (options?.rejectedAccessToken) {
        if (historyLogoutRef.current) return Promise.resolve(null);
        if (options.rejectedAccessToken !== current) {
          // Only replay ordinary requests after rotation within the same account.
          // A replacement login or shop switch must never inherit old requests.
          try {
            const rejected = parseJwt(options.rejectedAccessToken).identity;
            const replacement = current
              ? parseJwt(current).identity
              : undefined;
            const sameAccount =
              !historySessionRef.current &&
              !rejected?.acorn_legacy_viewer &&
              !replacement?.acorn_legacy_viewer &&
              !!rejected?.guid &&
              rejected.guid === replacement?.guid &&
              rejected.shop_id != null &&
              rejected.shop_id === replacement?.shop_id;
            return Promise.resolve(sameAccount ? current : null);
          } catch {
            return Promise.resolve(null);
          }
        }
      }
      if (historyLogoutRef.current) {
        const pending = logOut();
        setIsLoading(false);
        return pending.then(() => null);
      }
      if (current) {
        const payload = parseJwt(current);
        if (payload.identity?.acorn_legacy_viewer) {
          if (
            historyDeadlineRef.current !== null &&
            historyDeadlineRef.current <= performance.now()
          ) {
            clearAccessToken();
            return Promise.resolve(null);
          }
          if (!options?.rejectedAccessToken) return Promise.resolve(current);
        }
      }
      if (historySessionRef.current) {
        sessionStorage.setItem(HISTORY_SESSION_KEY, 'true');
        const grant = sessionStorage.getItem(HISTORY_GRANT_KEY);
        if (!isHistoryGrantId(grant)) {
          setIsLoading(false);
          return Promise.resolve(null);
        }
        const version = sessionVersionRef.current;
        if (refreshRequestRef.current?.sessionVersion === version)
          return refreshRequestRef.current.promise;
        const validated = historyValidationRef.current;
        if (
          current &&
          validated?.token === current &&
          performance.now() - validated.at < REFRESH_COOLDOWN_MS
        )
          return Promise.resolve(null);
        setHistoryResumeError(false);
        if (!current) setIsLoading(true);
        const promise = (async () => {
          try {
            const requestStarted = performance.now();
            const { data } = await axios.post<{
              access_token: string;
              expires_in: number;
            }>(
              `${internalApiHost}/v2/shops/history-handoff/session/${grant}`,
              {},
              { withCredentials: true, timeout: 15_000 },
            );
            if (sessionVersionRef.current !== version) return null;
            const claims = parseJwt(data.access_token);
            if (
              claims.jti !== grant ||
              !claims.identity?.acorn_legacy_viewer ||
              !Number.isFinite(data.expires_in) ||
              data.expires_in <= 0
            )
              throw new Error('Invalid history session');
            const deadline =
              performance.now() +
              Math.max(
                0,
                data.expires_in * 1000 - (performance.now() - requestStarted),
              );
            historyDeadlineRef.current =
              historyDeadlineRef.current === null
                ? deadline
                : Math.min(historyDeadlineRef.current, deadline);
            if (current)
              historyValidationRef.current = {
                token: data.access_token,
                at: performance.now(),
              };
            if (data.access_token !== current)
              setAccessToken(data.access_token);
            return data.access_token;
          } catch (error) {
            if (sessionVersionRef.current === version) {
              if (isTransientRefreshError(error)) {
                if (current)
                  historyValidationRef.current = {
                    token: current,
                    at: performance.now(),
                  };
                setHistoryResumeError(true);
              } else clearAccessToken();
            }
            return null;
          } finally {
            if (refreshRequestRef.current?.sessionVersion === version) {
              refreshRequestRef.current = null;
              if (!current) setIsLoading(false);
            }
          }
        })();
        refreshRequestRef.current = { sessionVersion: version, promise };
        return promise;
      }
      const requestSessionVersion = sessionVersionRef.current;
      if (refreshRequestRef.current?.sessionVersion === requestSessionVersion) {
        return refreshRequestRef.current.promise;
      }

      // Skip refresh if we recently attempted one (prevents rapid refresh loops)
      const isWithinCooldown =
        Date.now() - lastRefreshTimeRef.current < REFRESH_COOLDOWN_MS;
      if (isWithinCooldown && !options?.force) {
        return Promise.resolve(null);
      }
      lastRefreshTimeRef.current = Date.now();

      const promise = (async () => {
        try {
          const requestToken = () =>
            axios.post<{ access_token?: string }>(
              `${internalApiHost}${REFRESH_TOKEN_ENDPOINT}`,
              {},
              createRefreshTokenConfig(),
            );
          const response = await requestToken().catch(
            async (error: unknown) => {
              if (sessionVersionRef.current !== requestSessionVersion)
                return null;
              if (!isTransientRefreshError(error)) throw error;
              // Keep one shared refresh pending; readiness still blocks stale claims.
              await new Promise<void>((resolve) => {
                setTimeout(resolve, REFRESH_RETRY_DELAY_MS);
              });
              if (sessionVersionRef.current !== requestSessionVersion)
                return null;
              return requestToken();
            },
          );

          if (!response || sessionVersionRef.current !== requestSessionVersion)
            return null;
          const token = response.data.access_token;

          if (token) {
            setAccessToken(token);
            return token;
          }

          setAccessToken(null);
          return null;
        } catch (error: any) {
          if (sessionVersionRef.current !== requestSessionVersion) return null;
          // Don't clear the token: that would route to login before the reload
          // reaches Access's SSO redirect.
          if (
            isAccessExpired(error.response?.status, error.response?.headers) &&
            reloadForAccessExpiry() === 'reloading'
          ) {
            return null;
          }

          if (error.response?.status !== 401) {
            console.error(error.response?.data?.msg || error.message);
          }

          setAccessToken(null);
          return null;
        } finally {
          // Saving a session keeps this slot; only a newer refresh replaces it.
          if (
            refreshRequestRef.current?.sessionVersion === requestSessionVersion
          ) {
            refreshRequestRef.current = null;
            setIsLoading(false);
          }
        }
      })();
      refreshRequestRef.current = {
        sessionVersion: requestSessionVersion,
        promise,
      };
      return promise;
    },
    [clearAccessToken, logOut, setAccessToken],
  );

  // TODO: Should validate the token and type the return value here
  const getAccessTokenPayload = useCallback(() => {
    if (!accessToken) return;
    return parseJwt(accessToken);
  }, [accessToken]);

  const getShopId = useCallback(() => {
    const accessTokenPayload = getAccessTokenPayload();
    return accessTokenPayload?.shop_id as number;
  }, [getAccessTokenPayload]);

  const getUserPermissions = useCallback((): string[] | undefined => {
    const accessTokenPayload = getAccessTokenPayload();
    return accessTokenPayload?.identity?.permissions;
  }, [getAccessTokenPayload]);

  const userHasPermission = useCallback(
    (permission: string): boolean => {
      const permissions = getUserPermissions();
      return permissions ? permissions.includes(permission) : false;
    },
    [getUserPermissions],
  );

  const getLegacyUserRoles = useCallback((): LegacyRoles[] => {
    const accessTokenPayload = getAccessTokenPayload();
    return accessTokenPayload?.identity?.roles || [];
  }, [getAccessTokenPayload]);

  const userHasLegacyRole = useCallback(
    (role: LegacyRoles): boolean => {
      const roles = getLegacyUserRoles();
      return roles ? roles.includes(role) : false;
    },
    [getLegacyUserRoles],
  );

  const getMaxViewLevelForUser = useCallback(
    (user: Partial<User>) => {
      if (userHasLegacyRole(LEGACY_ROLES.AI_ALPHA_TESTER)) {
        return VIEW_OPTIONS.AI_DEBUGGER;
      }

      if (
        user.is_admin ||
        userHasPermission(PERMISSIONS.limit_sms_sales_agent_access)
      ) {
        return VIEW_OPTIONS.SALES_ASSOCIATE;
      }

      return VIEW_OPTIONS.MERCHANT;
    },
    [userHasLegacyRole, userHasPermission],
  );

  const authApi = useMemo(
    () => ({
      accessToken,
      sessionVersion,
      isLoading,
      isLoggedIn,
      isHistorySession,
      historyResumeError,
      historyLogoutPending,
      historyLogoutInFlight,
      failHistoryBootstrap,
      clearAccessToken,
      saveAccessToken,
      refreshAccessToken,
      logOut,
      getAccessTokenPayload,
      getShopId,
      getUserPermissions,
      userHasPermission,
      getLegacyUserRoles,
      userHasLegacyRole,
      getMaxViewLevelForUser,
    }),
    [
      accessToken,
      sessionVersion,
      isHistorySession,
      isLoading,
      historyResumeError,
      historyLogoutPending,
      historyLogoutInFlight,
      failHistoryBootstrap,
      isLoggedIn,
      clearAccessToken,
      saveAccessToken,
      refreshAccessToken,
      logOut,
      getAccessTokenPayload,
      getShopId,
      getUserPermissions,
      userHasPermission,
      getLegacyUserRoles,
      userHasLegacyRole,
      getMaxViewLevelForUser,
    ],
  );

  const initRefreshCycle = useCallback(
    (accessToken: string) => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      const jwtPayload = parseJwt(accessToken);
      const expiresAt = jwtPayload?.exp;
      if (jwtPayload.identity?.acorn_legacy_viewer) {
        const remaining = Math.max(
          (historyDeadlineRef.current ?? performance.now()) - performance.now(),
          0,
        );
        refreshTimeoutRef.current = setTimeout(clearAccessToken, remaining);
        return;
      }

      if (expiresAt) {
        const now = new Date();
        const timeUntilExpiration = expiresAt * 1000 - now.getTime();

        // Refresh the token 1 minute before expiration (or immediately if less than 1 minute remaining)
        const refreshBuffer = 60000; // 1 minute in milliseconds
        const refreshTime = Math.max(timeUntilExpiration - refreshBuffer, 0);

        if (timeUntilExpiration > 0) {
          const timeout = setTimeout(() => {
            refreshAccessToken();
          }, refreshTime);
          refreshTimeoutRef.current = timeout;
        }
      }
    },
    [clearAccessToken, refreshAccessToken],
  );

  // The utility functions defined in the Requests class and the axios interceptors need to plug into the React runtime in order
  // to access the auth context. We pass the refresh function to the axios client so it can handle 401s.
  useEffect(() => {
    initializeAxiosClients(accessToken, refreshAccessToken);
    Requests.setAuthApi(authApi);

    if (accessToken) {
      initRefreshCycle(accessToken);
    }

    // Cleanup function to clear timeout on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, initRefreshCycle, refreshAccessToken]);

  return <Provider value={authApi}>{children}</Provider>;
};

export const withAuth = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const authApi = useAuth();

    return <Component {...props} auth={authApi} />;
  };
};

export default useAuth;
